import { api } from './client'

export type CandidatoAUnir = {
  contatoId: number
  contato: string | null
  candidatoId: number
  candidato: string | null

  /** O que os dois têm em comum, em texto legível. Quem decide precisa ler isto. */
  provas: string[]
}

export type IdentificadorDoContato = {
  tipo: string
  valor: string
  canal: string | null
  ultimoVistoEm: string
}

export type CanalDoContato = {
  canal: string
  tipo: string
  conversas: number
  ultima: string
}

/** Um dos dois lados de uma união em potencial. */
export type LadoDoContato = {
  id: number
  nome: string | null
  criadoEm: string
  vinculadoAoContatoId: number | null
  identificadores: IdentificadorDoContato[]

  /**
   * 🔴 Separado por canal, sempre. Uma linha do tempo única esconde por onde
   * cada coisa foi dita — e é justamente isso que se está decidindo.
   */
  porCanal: CanalDoContato[]
}

/** Um contato encontrado pela busca da união manual. */
export type ContatoNaBusca = {
  id: number
  nome: string | null
  criadoEm: string

  /** Preenchido quando o contato já foi absorvido por outro — ele não pode ser unido de novo. */
  vinculadoAoContatoId: number | null

  identificadores: { tipo: string; valor: string }[]
  conversas: number
  ultimaAtividade: string | null
}

/** Um atendimento do acervo migrado, para o vínculo manual. */
export type AtendimentoNaBusca = {
  protocolo: number
  nome: string | null
  numero: string | null
  data: string | null
  mensagens: number

  /** A qual contato do portal ele já está ligado, se estiver. */
  contatoId: number | null
}

export const contatos = {
  candidatos: () => api.get<CandidatoAUnir[]>('/contatos/candidatos'),

  lados: (id: number, candidatoId: number) =>
    api.get<LadoDoContato[]>(`/contatos/${id}/lados?candidatoId=${candidatoId}`),

  /**
   * 🔴 A porta que faltava (069): buscar cada lado explicitamente.
   *
   * Telegram e Instagram não dão telefone, e o id do Instagram é escopado por
   * aplicativo — sem isto, todo contato desses canais nasce fragmentado para
   * sempre, e nenhuma melhoria no comparador resolve.
   */
  buscar: (termo: string) =>
    api.get<ContatoNaBusca[]>(`/contatos/buscar?termo=${encodeURIComponent(termo)}`),

  /**
   * Une sob confirmação explícita. Recusa marcas diferentes com o motivo.
   *
   * A justificativa é opcional e vai para a auditoria: união sugerida tem prova
   * automática, a manual não tem nenhuma — sem o registro, meses depois ninguém
   * sabe por que dois históricos viraram um.
   */
  unir: (id: number, candidatoId: number, justificativa?: string) =>
    api.post<void>(`/contatos/${id}/unir/${candidatoId}`, { justificativa: justificativa || null }),

  /** Devolve ao estado anterior, com os identificadores na origem. */
  desfazer: (contatoId: number) =>
    api.post<void>(`/contatos/vinculos/${contatoId}/desfazer`, {}),

  /** Atendimentos do acervo migrado, por número ou nome. */
  buscarNoAcervo: (termo: string) =>
    api.get<AtendimentoNaBusca[]>(`/contatos/acervo/buscar?termo=${encodeURIComponent(termo)}`),

  /** Liga um atendimento do acervo a um contato do portal — reversível e auditado. */
  vincularAcervo: (contatoId: number, protocolo: number, justificativa?: string) =>
    api.post<void>(`/contatos/${contatoId}/acervo/${protocolo}`, {
      justificativa: justificativa || null,
    }),

  /** Desfaz o vínculo manual com o acervo. */
  desvincularAcervo: (contatoId: number, protocolo: number) =>
    api.delete<void>(`/contatos/${contatoId}/acervo/${protocolo}`),
}
